import { useTranslations } from '@vocab/react';
import { Pagination, Stack, Tiles } from 'braid-design-system';
import { useEffect, useState } from 'react';

import { Section, type Jobs } from '../../../';
import type { PaginationType } from '../../../analytics/types';

import translations from './.vocab';
import { JobCard } from './JobCard';

interface Props {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  mode: 'view' | 'edit';
  model: Jobs;
  scrollToTop: () => void;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
  setSmallerTitle?: boolean;
}

export function JobsSection({
  mode,
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
  scrollToTop,
  setSmallerTitle = false,
}: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [jobs, setJobs] = useState(model?.jobs || []);
  const [totalJobs, setTotalJobs] = useState(0);
  const { t } = useTranslations(translations);

  useEffect(() => {
    async function loadJobs() {
      try {
        if (model?.organisationId) {
          const response = await getPaginatedJobs(
            model.organisationId,
            currentPage,
          );
          const { jobs: result = [], totalJobs: totalJobCount = 0 } =
            response ?? {};
          setJobs(result);
          setTotalJobs(totalJobCount);
        } else {
          setJobs(model?.jobs || []);
          setTotalJobs(model?.totalJobs || 0);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading jobs', {
          organisationId: model?.organisationId,
          error,
        });
        setJobs(model?.jobs || []);
        setTotalJobs(model?.totalJobs || 0);
      }
    }
    void loadJobs();
  }, [currentPage, getPaginatedJobs, model]);

  const totalPageCount = Math.ceil(totalJobs / model?.jobs.length || 1);

  const onPaginationLinkClick =
    (_: PaginationType, pageNumber: number) =>
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      trackJobsPaginationClicked({
        pageNumber,
        searchTotalCount: totalJobs,
      });
      setCurrentPage(pageNumber);
      setTimeout(scrollToTop, 300);
    };

  return (
    <Section mode={mode} padding="none" paddingBottom="none">
      <Stack space="large">
        <Tiles columns={[1, 2]} space={setSmallerTitle ? 'medium' : 'small'}>
          {jobs.map((job) => (
            <JobCard {...job} key={job.id} setSmallerTitle={setSmallerTitle} />
          ))}
        </Tiles>
        {totalPageCount > 1 && (
          <Pagination
            page={currentPage}
            total={totalPageCount}
            linkProps={({ page, type }) => ({
              href: `#`,
              onClick: onPaginationLinkClick(type, page),
            })}
            label={t('Jobs pagination')}
            nextLabel={t('Next')}
            previousLabel={t('Previous')}
          />
        )}
      </Stack>
    </Section>
  );
}
